import './CreateQuestion.css';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MultichoiceForm from './QuestionForms/MultichoiceForm';
import MultipleCorrectForm from './QuestionForms/MultipleCorrectForm';
import FillInForm from './QuestionForms/FillInForm';
import { getQuestionById, updateMultichoiceQuestion, updateMultipleCorrectQuestion, updateFillInQuestion } from '../../services/questionService';
import { fetchSimplifiedBookById } from '../../services/bookService';
import { fetchSimplifiedAuthorById } from '../../services/authorService';

const QUESTION_TYPE_LABELS = {
    'Multichoice': 'Výběr z možností',
    'MULTICHOICE': 'Výběr z možností',
    'MultipleCorrect': 'Více správných odpovědí',
    'MULTIPLE_CORRECT': 'Více správných odpovědí',
    'MULTIPLECORRECT': 'Více správných odpovědí',
    'FillIn': 'Doplňovačka',
    'FILL_IN': 'Doplňovačka',
    'Linking': 'Propojovačka',
    'LINKING': 'Propojovačka',
    'Timeline': 'Časová osa',
    'TIMELINE': 'Časová osa',
    'FindInText': 'Najít v textu',
    'FIND_IN_TEXT': 'Najít v textu'
};

function EditQuestion() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [questionData, setQuestionData] = useState(null);
    const [questionType, setQuestionType] = useState(null);
    const [relatedBooks, setRelatedBooks] = useState([]);
    const [relatedAuthors, setRelatedAuthors] = useState([]);

    useEffect(() => {
        loadQuestion();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadQuestion = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getQuestionById(id);
            setQuestionData(data);

            // Use questionType from backend, or detect from data structure if null
            let type = data.questionType;
            if (!type) {
                // Detect type from data structure for old questions
                if (data.choices && typeof data.correctChoice === 'number') {
                    type = 'Multichoice';
                } else if (data.choices && Array.isArray(data.correctChoices)) {
                    type = 'MultipleCorrect';
                } else if (data.blanks) {
                    type = 'FillIn';
                } else if (data.pairs) {
                    type = 'Linking';
                } else if (data.events) {
                    type = 'Timeline';
                } else if (data.textWithErrors) {
                    type = 'FindInText';
                }
            }

            setQuestionType(type);

            // Fetch simplified book and author objects
            const books = [];
            const authors = [];

            if (data.readingJournalIds && data.readingJournalIds.length > 0) {
                for (const bookId of data.readingJournalIds) {
                    try {
                        const book = await fetchSimplifiedBookById(bookId);
                        books.push(book);
                    } catch (err) {
                        console.error(`Failed to fetch book ${bookId}:`, err);
                    }
                }
            }

            if (data.authorIds && data.authorIds.length > 0) {
                for (const authorId of data.authorIds) {
                    try {
                        const author = await fetchSimplifiedAuthorById(authorId);
                        authors.push(author);
                    } catch (err) {
                        console.error(`Failed to fetch author ${authorId}:`, err);
                    }
                }
            }

            setRelatedBooks(books);
            setRelatedAuthors(authors);
        } catch (err) {
            console.error('Error loading question:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/admin/manage-questions');
    };

    const handleSubmit = async (formData) => {
        try {
            if (questionType?.toUpperCase() === 'MULTICHOICE') {
                // Find the index of the correct answer
                const correctIndex = formData.choices.findIndex(
                    choice => choice.id === formData.correctAnswer
                );

                const backendData = {
                    title: formData.questionText,
                    authorIds: formData.relatedAuthors || [],
                    readingJournalIds: formData.relatedBooks || [],
                    choices: formData.choices.map(choice => ({
                        text: choice.text,
                        notes: choice.note
                    })),
                    correctChoice: correctIndex
                };

                await updateMultichoiceQuestion(id, backendData);

                alert('Otázka byla úspěšně upravena!');
                navigate('/admin/manage-questions');
            } else if (questionType?.toUpperCase() === 'MULTIPLE_CORRECT' || questionType?.toUpperCase() === 'MULTIPLECORRECT') {
                // Find indices of all correct answers
                const correctIndices = formData.correctAnswers.map(answerId =>
                    formData.choices.findIndex(choice => choice.id === answerId)
                );

                const backendData = {
                    title: formData.questionText,
                    authorIds: formData.relatedAuthors || [],
                    readingJournalIds: formData.relatedBooks || [],
                    choices: formData.choices.map(choice => ({
                        text: choice.text,
                        notes: choice.note
                    })),
                    correctChoices: correctIndices,
                    correctCount: formData.correctCount || null
                };

                await updateMultipleCorrectQuestion(id, backendData);

                alert('Otázka byla úspěšně upravena!');
                navigate('/admin/manage-questions');
            } else if (questionType?.toUpperCase() === 'FILL_IN' || questionType?.toUpperCase() === 'FILLIN') {
                const backendData = {
                    title: formData.questionText,
                    authorIds: formData.relatedAuthors || [],
                    readingJournalIds: formData.relatedBooks || [],
                    text: formData.text,
                    blankPosition: formData.blankPosition,
                    correctAnswers: formData.correctAnswers
                };

                await updateFillInQuestion(id, backendData);

                alert('Otázka byla úspěšně upravena!');
                navigate('/admin/manage-questions');
            } else {
                throw new Error('Úprava tohoto typu otázky zatím není podporována');
            }
        } catch (error) {
            console.error('Error updating question:', error);
            alert(`Nepodařilo se upravit otázku: ${error.message}`);
        }
    };

    const handleCancel = () => {
        navigate('/admin/manage-questions');
    };

    // Transform backend data to form data
    const getInitialFormData = () => {
        if (!questionData) {
            return null;
        }

        if (questionType?.toUpperCase() === 'MULTICHOICE') {
            // Generate choice IDs (a, b, c, d, ...)
            const choicesWithIds = questionData.choices.map((choice, index) => ({
                id: String.fromCharCode(97 + index), // 'a', 'b', 'c', ...
                text: choice.text || '',
                note: choice.notes || choice.note || '' // Backend uses 'notes', fallback to 'note'
            }));

            // Get the ID of the correct choice
            const correctChoiceId = choicesWithIds[questionData.correctChoice]?.id || '';

            // Transform authors and books to selectedItems format
            const selectedItems = [];

            relatedBooks.forEach(book => {
                selectedItems.push({
                    type: 'book',
                    data: book
                });
            });

            relatedAuthors.forEach(author => {
                selectedItems.push({
                    type: 'author',
                    data: author
                });
            });

            return {
                questionText: questionData.title,
                choices: choicesWithIds,
                correctAnswer: correctChoiceId,
                selectedItems: selectedItems
            };
        }

        if (questionType?.toUpperCase() === 'MULTIPLE_CORRECT' || questionType?.toUpperCase() === 'MULTIPLECORRECT') {
            // Generate choice IDs (a, b, c, d, ...)
            const choicesWithIds = questionData.choices.map((choice, index) => {
                const choiceId = String.fromCharCode(97 + index); // 'a', 'b', 'c', ...
                const isCorrect = questionData.correctChoices && questionData.correctChoices.includes(index);

                return {
                    id: choiceId,
                    text: choice.text || '',
                    note: choice.notes || choice.note || '',
                    isCorrect: isCorrect
                };
            });

            // Transform authors and books to selectedItems format
            const selectedItems = [];

            relatedBooks.forEach(book => {
                selectedItems.push({
                    type: 'book',
                    data: book
                });
            });

            relatedAuthors.forEach(author => {
                selectedItems.push({
                    type: 'author',
                    data: author
                });
            });

            return {
                questionText: questionData.title,
                choices: choicesWithIds,
                selectedItems: selectedItems,
                showCorrectCount: questionData.correctCount !== null && questionData.correctCount !== undefined,
                correctCount: questionData.correctCount
            };
        }

        if (questionType?.toUpperCase() === 'FILL_IN' || questionType?.toUpperCase() === 'FILLIN') {
            const selectedItems = [];

            relatedBooks.forEach(book => {
                selectedItems.push({
                    type: 'book',
                    data: book
                });
            });

            relatedAuthors.forEach(author => {
                selectedItems.push({
                    type: 'author',
                    data: author
                });
            });

            // Reconstruct blankIndices from blankPosition and correctAnswers
            const blankIndices = [];
            if (questionData.text && questionData.correctAnswers?.length > 0) {
                const textWords = questionData.text.trim().split(/\s+/);
                // Try to find each correct answer in the text starting from blankPosition
                for (const answer of questionData.correctAnswers) {
                    const idx = textWords.indexOf(answer, 0);
                    if (idx !== -1 && !blankIndices.includes(idx)) {
                        blankIndices.push(idx);
                    }
                }
                // Fallback: if nothing found, use blankPosition
                if (blankIndices.length === 0 && questionData.blankPosition != null) {
                    blankIndices.push(questionData.blankPosition);
                }
            }

            return {
                questionText: questionData.title,
                text: questionData.text || '',
                blankIndices: blankIndices,
                selectedItems: selectedItems
            };
        }

        return null;
    };

    if (loading) {
        return (
            <div className="create-question-page">
                <div className="create-question-container">
                    <button className="btn-back" onClick={handleBack}>
                        ← Zpět na správu otázek
                    </button>
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#eee' }}>
                        <p>Načítám otázku...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="create-question-page">
                <div className="create-question-container">
                    <button className="btn-back" onClick={handleBack}>
                        ← Zpět na správu otázek
                    </button>
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#e94560' }}>
                        <p>Chyba: {error}</p>
                        <button
                            onClick={loadQuestion}
                            style={{
                                marginTop: '1rem',
                                padding: '0.75rem 1.5rem',
                                backgroundColor: '#e94560',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Zkusit znovu
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const initialFormData = getInitialFormData();

    return (
        <div className="create-question-page">
            <div className="create-question-container">
                <button className="btn-back" onClick={handleBack}>
                    ← Zpět na správu otázek
                </button>

                <h1 className="page-title">Upravit otázku</h1>

                <div className="question-form-container">
                    <div className="selected-type-header">
                        <h2 className="selected-type-name">
                            {QUESTION_TYPE_LABELS[questionType] || questionType}
                        </h2>
                    </div>

                    {questionType?.toUpperCase() === 'MULTICHOICE' && initialFormData ? (
                        <MultichoiceForm
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            initialData={initialFormData}
                            isEdit={true}
                        />
                    ) : questionType?.toUpperCase() === 'MULTICHOICE' && !initialFormData ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#e94560' }}>
                            <p>Chyba při načítání dat formuláře</p>
                        </div>
                    ) : (questionType?.toUpperCase() === 'MULTIPLE_CORRECT' || questionType?.toUpperCase() === 'MULTIPLECORRECT') && initialFormData ? (
                        <MultipleCorrectForm
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            initialData={initialFormData}
                            isEdit={true}
                        />
                    ) : (questionType?.toUpperCase() === 'MULTIPLE_CORRECT' || questionType?.toUpperCase() === 'MULTIPLECORRECT') && !initialFormData ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#e94560' }}>
                            <p>Chyba při načítání dat formuláře</p>
                        </div>
                    ) : (questionType?.toUpperCase() === 'FILL_IN' || questionType?.toUpperCase() === 'FILLIN') && initialFormData ? (
                        <FillInForm
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            initialData={initialFormData}
                            isEdit={true}
                        />
                    ) : (questionType?.toUpperCase() === 'FILL_IN' || questionType?.toUpperCase() === 'FILLIN') && !initialFormData ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#e94560' }}>
                            <p>Chyba při načítání dat formuláře</p>
                        </div>
                    ) : questionType ? (
                        <p className="form-info">
                            Úprava tohoto typu otázky bude implementována v dalším kroku.
                        </p>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
                            <p>Načítám typ otázky...</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default EditQuestion;
